import React, { ElementType, RefObject } from 'react';
import {
  Badge,
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { AddIcon, ArrowBackIcon } from '@chakra-ui/icons';
import { Search } from 'lucide-react';
import { FiArrowUpRight, FiCheckCircle, FiClock, FiPauseCircle, FiSlash, FiAlertTriangle } from 'react-icons/fi';
import { Task } from '../../../utils/types';

export const taskAccent = '#166534';
export const taskSurface = 'white';
export const taskBorder = 'gray.200';

export const formatTaskDate = (value?: string | null) => {
  if (!value) return 'No date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No date';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

export const formatTaskDateTime = (value?: string | null) => {
  if (!value) return 'No date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No date';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

export const isTaskComplete = (task?: Task | null) =>
  task?.status === 'completed' || task?.status === 'cancelled';

export const isTaskOverdue = (task?: Task | null) => {
  if (!task?.due_date || isTaskComplete(task)) return false;
  const due = new Date(task.due_date);
  if (Number.isNaN(due.getTime())) return false;
  const now = new Date();
  due.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);
  return due < now;
};

export const isTaskDueToday = (task?: Task | null) => {
  if (!task?.due_date || isTaskComplete(task)) return false;
  const due = new Date(task.due_date);
  if (Number.isNaN(due.getTime())) return false;
  const now = new Date();
  return (
    due.getFullYear() === now.getFullYear() &&
    due.getMonth() === now.getMonth() &&
    due.getDate() === now.getDate()
  );
};

export const getTaskStatusMeta = (status?: string) => {
  switch (status) {
    case 'completed':
      return { label: 'Completed', colorScheme: 'green', icon: FiCheckCircle };
    case 'in_progress':
      return { label: 'In progress', colorScheme: 'blue', icon: FiArrowUpRight };
    case 'on_hold':
      return { label: 'On hold', colorScheme: 'purple', icon: FiPauseCircle };
    case 'cancelled':
      return { label: 'Cancelled', colorScheme: 'red', icon: FiSlash };
    case 'pending':
    default:
      return { label: 'Pending', colorScheme: 'orange', icon: FiClock };
  }
};

export const getTaskPriorityMeta = (priority?: string) => {
  switch (priority) {
    case 'urgent':
      return { label: 'Urgent', colorScheme: 'red' };
    case 'high':
      return { label: 'High', colorScheme: 'pink' };
    case 'low':
      return { label: 'Low', colorScheme: 'gray' };
    case 'medium':
    default:
      return { label: 'Medium', colorScheme: 'orange' };
  }
};

export const getTaskUrgencyLabel = (task: Task) => {
  if (isTaskOverdue(task)) return 'Overdue';
  if (isTaskDueToday(task)) return 'Due today';
  return formatTaskDate(task.due_date);
};

export const getTaskUrgencyColor = (task: Task) => {
  if (isTaskOverdue(task)) return 'red.600';
  if (isTaskDueToday(task)) return 'orange.600';
  return 'gray.500';
};

export const getTaskSearchIndex = (task: Task) =>
  [
    task.title,
    task.description,
    task.remarks,
    task.assigned_to_name,
    task.created_by_name,
    task.priority,
    task.status,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

export const TaskShell = ({
  compact = false,
  children,
}: {
  compact?: boolean;
  children: React.ReactNode;
}) => (
  <Box bg="#F6F8FB" minH="100vh" px={{ base: 4, md: compact ? 6 : 6, xl: compact ? 8 : 8 }} py={{ base: 4, md: 6 }}>
    <Box maxW={compact ? '1120px' : '1280px'} mx="auto">
      {children}
    </Box>
  </Box>
);

export const TaskHero = ({
  eyebrow,
  title,
  description,
  primaryAction,
  secondaryAction,
}: {
  eyebrow: string;
  title: string;
  description: string;
  primaryAction?: React.ReactNode;
  secondaryAction?: React.ReactNode;
}) => (
  <Box
    bg="white"
    borderWidth="1px"
    borderColor={taskBorder}
    borderRadius={{ base: '22px', md: '26px' }}
    px={{ base: 5, md: 7 }}
    py={{ base: 5, md: 6 }}
    boxShadow="0 20px 44px rgba(15, 23, 42, 0.05)"
    mb={6}
  >
    <Stack
      direction={{ base: 'column', lg: 'row' }}
      justify="space-between"
      align={{ base: 'flex-start', lg: 'center' }}
      spacing={4}
    >
      <Box maxW="760px">
        <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.14em" fontWeight="800" color="gray.500" mb={2}>
          {eyebrow}
        </Text>
        <Text fontSize={{ base: '3xl', md: '4xl' }} lineHeight={{ base: '1.1', md: '1.05' }} fontWeight="800" color="gray.900">
          {title}
        </Text>
        <Text mt={3} color="gray.600" maxW="620px" lineHeight="1.65">
          {description}
        </Text>
      </Box>
      <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} w={{ base: '100%', lg: 'auto' }}>
        {secondaryAction}
        {primaryAction}
      </Stack>
    </Stack>
  </Box>
);

export const TaskPrimaryButton = ({
  children,
  leftIcon,
  onClick,
  type,
  isLoading,
  isDisabled,
}: {
  children: React.ReactNode;
  leftIcon?: React.ReactElement;
  onClick?: () => void;
  type?: 'button' | 'submit';
  isLoading?: boolean;
  isDisabled?: boolean;
}) => (
  <Button
    type={type}
    onClick={onClick}
    leftIcon={leftIcon}
    isLoading={isLoading}
    isDisabled={isDisabled}
    bg={taskAccent}
    color="white"
    borderRadius="16px"
    px={5}
    _hover={{ bg: '#14532D' }}
    _active={{ bg: '#166534' }}
    w={{ base: '100%', sm: 'auto' }}
  >
    {children}
  </Button>
);

export const TaskSecondaryButton = ({
  children,
  leftIcon,
  onClick,
}: {
  children: React.ReactNode;
  leftIcon?: React.ReactElement;
  onClick?: () => void;
}) => (
  <Button
    variant="outline"
    leftIcon={leftIcon}
    onClick={onClick}
    borderRadius="16px"
    borderColor="gray.300"
    color="gray.700"
    w={{ base: '100%', sm: 'auto' }}
  >
    {children}
  </Button>
);

export const TaskMetricStrip = ({
  items,
}: {
  items: Array<{ label: string; value: string; helper: string; icon: ElementType; color?: string }>;
}) => (
  <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing={4} mb={6}>
    {items.map((item) => (
      <Box
        key={item.label}
        bg="white"
        borderWidth="1px"
        borderColor={taskBorder}
        borderRadius="20px"
        px={5}
        py={5}
        boxShadow="0 16px 36px rgba(15, 23, 42, 0.04)"
      >
        <HStack justify="space-between" align="flex-start" mb={4}>
          <Box
            boxSize="52px"
            borderRadius="16px"
            bg="gray.50"
            color={item.color || taskAccent}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={item.icon} boxSize={5.5} />
          </Box>
          <Badge borderRadius="full" bg="gray.50" color="gray.600" px={2.5} py={1}>
            Live
          </Badge>
        </HStack>
        <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.12em" fontWeight="800" color="gray.500" mb={2}>
          {item.label}
        </Text>
        <Text fontSize={{ base: '2xl', md: '3xl' }} lineHeight="1" fontWeight="800" color="gray.900" mb={2}>
          {item.value}
        </Text>
        <Text fontSize="sm" color="gray.500">
          {item.helper}
        </Text>
      </Box>
    ))}
  </SimpleGrid>
);

export const TaskSectionCard = ({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box
    bg={taskSurface}
    borderWidth="1px"
    borderColor={taskBorder}
    borderRadius={{ base: '20px', md: '24px' }}
    px={{ base: 4, md: 6 }}
    py={{ base: 4, md: 5 }}
    boxShadow="0 18px 40px rgba(15, 23, 42, 0.05)"
  >
    <Stack
      direction={{ base: 'column', md: 'row' }}
      justify="space-between"
      align={{ base: 'flex-start', md: 'center' }}
      mb={5}
      spacing={3}
    >
      <Box>
        <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="800" color="gray.900">
          {title}
        </Text>
        {description ? (
          <Text fontSize="sm" color="gray.500" mt={1}>
            {description}
          </Text>
        ) : null}
      </Box>
      {action}
    </Stack>
    {children}
  </Box>
);

export const TaskStatusBadge = ({ status }: { status?: string | null }) => {
  const meta = getTaskStatusMeta(status || 'pending');
  return (
    <Badge colorScheme={meta.colorScheme} borderRadius="full" px={3} py={1.5}>
      {meta.label}
    </Badge>
  );
};

export const TaskPriorityBadge = ({ priority }: { priority?: string | null }) => {
  const meta = getTaskPriorityMeta(priority || 'medium');
  return (
    <Badge colorScheme={meta.colorScheme} borderRadius="full" px={3} py={1.5}>
      {meta.label}
    </Badge>
  );
};

export const TaskCommandBar = ({
  search,
  onSearchChange,
  status,
  onStatusChange,
  priority,
  onPriorityChange,
  sort,
  onSortChange,
  inputRef,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  status: string;
  onStatusChange: (value: string) => void;
  priority: string;
  onPriorityChange: (value: string) => void;
  sort: string;
  onSortChange: (value: string) => void;
  inputRef?: RefObject<HTMLInputElement | null>;
}) => (
  <TaskSectionCard title="Task queue" description="Filter and sort the work list by urgency, ownership, and status.">
    <Stack direction={{ base: 'column', lg: 'row' }} spacing={3}>
      <InputGroup flex={1}>
        <InputLeftElement pointerEvents="none">
          <Search size={16} color="#718096" />
        </InputLeftElement>
        <Input
          ref={inputRef}
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search title, assignee, remarks"
          borderRadius="16px"
          bg="gray.50"
          borderColor="gray.200"
          _focusVisible={{
            borderColor: 'green.400',
            boxShadow: '0 0 0 1px rgba(22, 101, 52, 0.28)',
          }}
        />
      </InputGroup>

      <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} minW={{ lg: '520px' }}>
        <Select value={status} onChange={(event) => onStatusChange(event.target.value)} borderRadius="16px" bg="gray.50">
          <option value="all">All statuses</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
          <option value="on_hold">On hold</option>
          <option value="cancelled">Cancelled</option>
        </Select>
        <Select value={priority} onChange={(event) => onPriorityChange(event.target.value)} borderRadius="16px" bg="gray.50">
          <option value="all">All priorities</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </Select>
        <Select value={sort} onChange={(event) => onSortChange(event.target.value)} borderRadius="16px" bg="gray.50">
          <option value="due-asc">Due soonest</option>
          <option value="due-desc">Due latest</option>
          <option value="created-desc">Newest first</option>
          <option value="priority-desc">Highest priority</option>
          <option value="title-asc">Title A-Z</option>
        </Select>
      </Stack>
    </Stack>
  </TaskSectionCard>
);

export const TaskEmptyState = ({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) => (
  <VStack py={16} px={6} spacing={3} textAlign="center">
    <Box
      boxSize="56px"
      borderRadius="full"
      bg="gray.100"
      display="flex"
      alignItems="center"
      justifyContent="center"
      color="gray.500"
    >
      <Icon as={FiAlertTriangle as ElementType} boxSize={6} />
    </Box>
    <Text fontWeight="800" color="gray.900">
      {title}
    </Text>
    <Text fontSize="sm" color="gray.500" maxW="340px">
      {description}
    </Text>
    {action}
  </VStack>
);

export const TaskDashboardSkeleton = () => (
  <Stack spacing={6}>
    <Box bg="white" borderWidth="1px" borderColor={taskBorder} borderRadius="24px" p={6}>
      <Skeleton h="18px" w="120px" mb={4} />
      <Skeleton h="34px" w={{ base: '74%', md: '52%' }} mb={3} />
      <Skeleton h="16px" w={{ base: '88%', md: '60%' }} />
    </Box>
    <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing={4}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Box key={index} bg="white" borderWidth="1px" borderColor={taskBorder} borderRadius="20px" p={5}>
          <Skeleton h="16px" w="112px" mb={4} />
          <Skeleton h="28px" w="72px" mb={2} />
          <Skeleton h="12px" w="140px" />
        </Box>
      ))}
    </SimpleGrid>
    <Box bg="white" borderWidth="1px" borderColor={taskBorder} borderRadius="24px" p={6}>
      <Skeleton h="18px" w="140px" mb={5} />
      <Stack spacing={3}>
        {Array.from({ length: 6 }).map((_, index) => (
          <Box key={index}>
            <Skeleton h="56px" borderRadius="16px" />
          </Box>
        ))}
      </Stack>
    </Box>
  </Stack>
);

export const TaskField = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => (
  <Box>
    <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.12em" fontWeight="800" color="gray.500" mb={1.5}>
      {label}
    </Text>
    <Text color="gray.900" fontWeight="600">
      {value}
    </Text>
  </Box>
);

export const TaskFormSection = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <Box borderWidth="1px" borderColor="gray.200" borderRadius="20px" p={{ base: 4, md: 5 }} bg="white">
    <Text fontWeight="800" color="gray.900" mb={1}>
      {title}
    </Text>
    {description ? (
      <Text fontSize="sm" color="gray.500" mb={4}>
        {description}
      </Text>
    ) : null}
    <Stack spacing={4}>{children}</Stack>
  </Box>
);

export const TaskBackButton = ({ onClick }: { onClick: () => void }) => (
  <IconButton
    aria-label="Back"
    icon={<ArrowBackIcon />}
    onClick={onClick}
    variant="ghost"
    borderRadius="14px"
  />
);

export const TaskCreateButton = ({ onClick }: { onClick: () => void }) => (
  <Button
    onClick={onClick}
    leftIcon={<AddIcon />}
    bg={taskAccent}
    color="white"
    borderRadius="16px"
    _hover={{ bg: '#14532D' }}
  >
    New task
  </Button>
);
